"use client";

import React, { useState } from "react";
import { ShieldCheck, Barcode, Microscope, CircleCheck, ArrowRight, FileText, Search, Check, ClipboardList, Cpu } from "lucide-react";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { siteImage } from "@/lib/siteImages";
interface LocalizedItem {
  en: string;
  zh: string;
  es: string;
  de: string;
  fr: string;
  pt: string;
}
interface TraceabilityStep {
  id: string;
  stepNumber: string;
  title: LocalizedItem;
  description: LocalizedItem;
  icon: React.ElementType;
  highlights: LocalizedItem[];
}
const traceabilitySteps: TraceabilityStep[] = [{
  id: "heat-tracking",
  stepNumber: "01",
  title: {
    en: "Raw Material Heat Tracking",
    zh: "原材料熔炼炉号全程追溯",
    es: "Trazabilidad de colada de materia prima",
    de: "Schmelzenrückverfolgung des Rohmaterials",
    fr: "Traçabilité des coulées de matière première",
    pt: "Rastreabilidade de corrida da matéria-prima"
  },
  description: {
    en: "Every wire rod lot is assigned a unique heat number from master steel mills, guaranteeing 100% origin tracking prior to weaving.",
    zh: "每批次母材盘圆进厂均绑定大型特钢厂专属熔炼炉号 (Heat No.)，确保织造前实现完整的产地溯源。",
    es: "A cada lote de alambrón se le asigna un número de colada exclusivo de acería principal, garantizando el seguimiento de origen antes del tejido.",
    de: "Jedes Walzdrahtlos erhält eine eindeutige Schmelzennummer aus der Primärostahlhütte, die vor dem Weben eine vollständige Herkunftsverfolgung garantiert.",
    fr: "Chaque lot de fil machine se voit attribuer un numéro de coulée unique provenant d'aciéries de référence, garantissant le suivi d'origine avant tissage.",
    pt: "Cada lote de fio-máquina recebe um número de corrida exclusivo da aciaria primária, garantindo o rastreamento de origem antes da tecelagem."
  },
  icon: Barcode,
  highlights: [{
    en: "Mill Test Report (MTR) assigned",
    zh: "绑定钢厂出厂质检报告 (MTR)",
    es: "Informe de ensayo de fábrica (MTR) asignado",
    de: "Werksprüfzeugnis (MTR) zugeordnet",
    fr: "Certificat d'essais usine (MTR) attribué",
    pt: "Relatório de teste de usina (MTR) atribuído"
  }, {
    en: "Melt source verification",
    zh: "特钢冶炼源头正材核验",
    es: "Verificación del origen de colada",
    de: "Verifizierung der Schmelzquelle",
    fr: "Vérification de la source de fusion",
    pt: "Verificação da fonte de fusão"
  }, {
    en: "Ladle chemical analysis",
    zh: "钢包熔融阶段化学成分复检",
    es: "Análisis químico de cuchara",
    de: "Pfannenanalyse",
    fr: "Analyse chimique sur poche",
    pt: "Análise química de panela"
  }]
}, {
  id: "pmi-testing",
  stepNumber: "02",
  title: {
    en: "Positive Material Identification (PMI)",
    zh: "正材光谱成分分析 (PMI)",
    es: "Identificación positiva de materiales (PMI)",
    de: "Positive Materialidentifikation (PMI)",
    fr: "Identification positive des matériaux (PMI)",
    pt: "Identificação positiva de materiais (PMI)"
  },
  description: {
    en: "Handheld XRF spectrometry verifies alloy composition (Cr, Ni, Mo content) at wire drawing, weaving, and packaging stages.",
    zh: "在冷拉拔、整经织网及成品分卷包装关键工序，利用便携式 XRF 荧光光谱仪精准验证 Cr、Ni、Mo 合金元素含量。",
    es: "La espectrometría XRF portátil verifica la composición de la aleación (contenido de Cr, Ni, Mo) en el trefilado, tejido y embalaje.",
    de: "Handgehaltene RFA-Spektrometrie überprüft die Legierungszusammensetzung (Cr-, Ni-, Mo-Gehalt) beim Drahtziehen, Weben und Verpacken.",
    fr: "La spectrométrie XRF portable contrôle la composition de l'alliage (teneurs en Cr, Ni, Mo) au tréfilage, au tissage et au conditionnement.",
    pt: "A espectrometria portátil por XRF verifica a composição da liga (teores de Cr, Ni, Mo) na trefilação, tecelagem e embalagem."
  },
  icon: Microscope,
  highlights: [{
    en: "Non-destructive XRF screening",
    zh: "无损 XRF 荧光光谱快速筛查",
    es: "Detección no destructiva por XRF",
    de: "Zerstörungsfreie RFA-Prüfung",
    fr: "Contrôle non destructif par XRF",
    pt: "Triagem não destrutiva por XRF"
  }, {
    en: "Zero alloy mix-up guarantee",
    zh: "严格防混料控制机制",
    es: "Garantía de cero mezcla de aleaciones",
    de: "Sicherung gegen Werkstoffverwechslung",
    fr: "Garantie anti-confusion d'alliages",
    pt: "Garantia de zero mistura de ligas"
  }, {
    en: "Grade verification (304, 316L, 904L, Inconel)",
    zh: "牌号复核 (304, 316L, 904L, Inconel)",
    es: "Verificación de grado (304, 316L, 904L, Inconel)",
    de: "Sortenprüfung (304, 316L, 904L, Inconel)",
    fr: "Vérification des nuances (304, 316L, 904L, Inconel)",
    pt: "Verificação de graus (304, 316L, 904L, Inconel)"
  }]
}, {
  id: "en10204-cert",
  stepNumber: "03",
  title: {
    en: "EN 10204 3.1 / 3.2 Certification",
    zh: "EN 10204 3.1 / 3.2 质检认证",
    es: "Certificación EN 10204 3.1 / 3.2",
    de: "EN 10204 3.1 / 3.2 Zertifizierung",
    fr: "Certification EN 10204 3.1 / 3.2",
    pt: "Certificação EN 10204 3.1 / 3.2"
  },
  description: {
    en: "Comprehensive physical and mechanical inspection documentation delivered with every shipment according to international standards.",
    zh: "按国际标准随货提供包含实测力学性能与几何公差的权威出厂检验报告与出厂合格证。",
    es: "Documentación integral de inspección física y mecánica suministrada con cada envío según normas internacionales.",
    de: "Umfassende Dokumentation physikalischer und mechanischer Prüfungen, geliefert gemäß internationalen Standards.",
    fr: "Documentation complète d'inspection physique et mécanique fournie avec chaque expédition selon les normes internationales.",
    pt: "Documentação abrangente de inspeção física e mecânica fornecida com cada remessa de acordo com normas internacionais."
  },
  icon: FileText,
  highlights: [{
    en: "Independent quality sign-off",
    zh: "质量检验部门独立签署",
    es: "Aprobación de calidad independiente",
    de: "Unabhängige Qualitätsfreigabe",
    fr: "Validation qualité indépendante",
    pt: "Validação independente de qualidade"
  }, {
    en: "Tensile & elongation data",
    zh: "抗拉强度与延伸率实测数据",
    es: "Datos de tracción y elongación",
    de: "Zugfestigkeits- & Dehnungswerte",
    fr: "Données de traction et d'allongement",
    pt: "Dados de tração e alongamento"
  }, {
    en: "Aperture & wire diameter tolerances",
    zh: "网孔孔径与丝径尺寸公差",
    es: "Tolerancias de apertura y diámetro de hilo",
    de: "Maschenweiten- & Drahtdurchmessertoleranzen",
    fr: "Tolérances d'ouverture et de diamètre de fil",
    pt: "Tolerâncias de abertura e diâmetro de arame"
  }]
}, {
  id: "batch-qr",
  stepNumber: "04",
  title: {
    en: "Digital Batch Marking & QR Labeling",
    zh: "数字化批次二维码标签标识",
    es: "Marcado digital de lote y etiquetado QR",
    de: "Digitale Chargenkennzeichnung & QR-Etikettierung",
    fr: "Marquage numérique de lot et étiquetage QR",
    pt: "Marcação digital de lote e etiquetagem QR"
  },
  description: {
    en: "Roll cores and outer packaging carry durable scannable labels for immediate digital retrieval of test certificates in the field.",
    zh: "滤网卷芯与外包装张贴耐磨损可扫描二维码，支持现场快速扫码核对与质保证明文件调阅。",
    es: "Los tubos interiores y el embalaje exterior llevan etiquetas escaneables para la consulta digital inmediata de certificados en campo.",
    de: "Rollenkerne und Außenverpackungen tragen scanbare Etiketten zum sofortigen digitalen Abruf von Prüfzertifikaten vor Ort.",
    fr: "Les mandrins de rouleaux et l'emballage extérieur portent des étiquettes scannables pour un accès numérique immédiat aux certificats sur site.",
    pt: "Os tubos internos e embalagens externas possuem etiquetas escaneáveis para consulta digital imediata de certificados em campo."
  },
  icon: ShieldCheck,
  highlights: [{
    en: "Unique Lot ID tracking",
    zh: "唯一生产批号 (Lot ID) 标识",
    es: "Seguimiento por ID único de lote",
    de: "Rückverfolgung über eindeutige Los-ID",
    fr: "Suivi par identifiant unique de lot",
    pt: "Rastreamento por ID exclusivo de lote"
  }, {
    en: "Instant PDF documentation",
    zh: "出厂检验报告即时电子归档",
    es: "Documentación PDF instantánea",
    de: "Sofortige PDF-Dokumentation",
    fr: "Documentation PDF instantanée",
    pt: "Documentação PDF instantânea"
  }, {
    en: "Long-term archive preservation",
    zh: "质量档案长期受控留存",
    es: "Conservación de archivos a largo plazo",
    de: "Langzeitarchivierung der Prüfdaten",
    fr: "Archivage à long terme des données",
    pt: "Preservação de arquivos a longo prazo"
  }]
}];
const chemicalComposition = [{
  element: "Chromium (Cr)",
  standard: "16.00 - 18.00%",
  measured: "16.85%",
  pass: true
}, {
  element: "Nickel (Ni)",
  standard: "10.00 - 14.00%",
  measured: "10.22%",
  pass: true
}, {
  element: "Molybdenum (Mo)",
  standard: "2.00 - 3.00%",
  measured: "2.14%",
  pass: true
}, {
  element: "Carbon (C)",
  standard: "max 0.030%",
  measured: "0.021%",
  pass: true
}, {
  element: "Manganese (Mn)",
  standard: "max 2.00%",
  measured: "1.42%",
  pass: true
}];
export default function Traceability() {
  const [activeTab, setActiveTab] = useState<"chem" | "mech">("chem");
  const {
    lang
  } = useLanguage();
  const copy = {
    badge: {
      en: "100% Material Assurance",
      zh: "材料品质可靠保障",
      es: "Garantía de material al 100%",
      de: "100% Materialsicherheit",
      fr: "Garantie matière 100%",
      pt: "Garantia de material a 100%"
    },
    h2: {
      en: "Complete Lot Traceability & Material Certification",
      zh: "全流程批次追溯与材料质检认证体系",
      es: "Trazabilidad completa de lote y certificación de materiales",
      de: "Vollständige Chargenrückverfolgbarkeit & Materialzertifizierung",
      fr: "Traçabilité intégrale des lots et certification des matériaux",
      pt: "Rastreabilidade completa de lote e certificação de materiais"
    },
    lead: {
      en: "MutualFilter supports supply chain traceability; EN 10204 3.1 test certificates are available when specified in the order.",
      zh: "MutualFilter 支持完善的供应链质量溯源体系；EN 10204 3.1 检验报告可按订单要求提供。",
      es: "MutualFilter respalda la trazabilidad de la cadena de suministro; los certificados de ensayo EN 10204 3.1 están disponibles cuando se especifiquen en el pedido.",
      de: "MutualFilter unterstützt die Rückverfolgbarkeit in der Lieferkette; EN 10204 3.1 Prüfbescheinigungen sind auf Bestellung erhältlich.",
      fr: "MutualFilter garantit la traçabilité de sa chaîne logistique ; les certificats d'essais EN 10204 3.1 sont disponibles sur demande lors de la commande.",
      pt: "A MutualFilter oferece rastreabilidade na cadeia de suprimentos; certificados de teste EN 10204 3.1 estão disponíveis quando especificados no pedido."
    },
    phasePrefix: {
      en: "Phase",
      zh: "流程阶段",
      es: "Fase",
      de: "Phase",
      fr: "Phase",
      pt: "Fase"
    },
    liveSpecVerification: {
      en: "Live Spec Verification",
      zh: "实物检验数据样本",
      es: "Verificación de especificación en vivo",
      de: "Live-Spezifikationsprüfung",
      fr: "Vérification des spécifications en direct",
      pt: "Verificação de especificação ao vivo"
    },
    certTitle: {
      en: "EN 10204 Type 3.1 Certificate",
      zh: "EN 10204 3.1 检验证明书",
      es: "Certificado EN 10204 Tipo 3.1",
      de: "EN 10204 Typ 3.1 Abnahmeprüfzeugnis",
      fr: "Certificat EN 10204 Type 3.1",
      pt: "Certificado EN 10204 Tipo 3.1"
    },
    verifiedPass: {
      en: "VERIFIED PASS",
      zh: "质检合格",
      es: "VERIFICADO CONFORME",
      de: "BESTANDEN",
      fr: "CONFORME VÉRIFIÉ",
      pt: "VERIFICADO APROVADO"
    },
    heatNumberLabel: {
      en: "Heat Number",
      zh: "冶炼炉号",
      es: "Número de colada",
      de: "Schmelzennummer",
      fr: "Numéro de coulée",
      pt: "Número da corrida"
    },
    alloyGradeLabel: {
      en: "Alloy Grade",
      zh: "合金牌号",
      es: "Grado de aleación",
      de: "Legierungsgüte",
      fr: "Nuance d'alliage",
      pt: "Grau da liga"
    },
    meshCountLabel: {
      en: "Mesh Count",
      zh: "丝网规格",
      es: "Malla / Especificación",
      de: "Maschenzahl",
      fr: "Nombre de mailles",
      pt: "Malha / Fios"
    },
    lotRollIdLabel: {
      en: "Lot / Roll ID",
      zh: "批次 / 卷号",
      es: "ID de lote / rollo",
      de: "Chargen- / Rollen-ID",
      fr: "ID lot / rouleau",
      pt: "ID de lote / rolo"
    },
    tabChem: {
      en: "Chemical Analysis (XRF)",
      zh: "化学成分分析 (XRF)",
      es: "Análisis químico (XRF)",
      de: "Chemische Analyse (RFA)",
      fr: "Analyse chimique (XRF)",
      pt: "Análise química (XRF)"
    },
    tabMech: {
      en: "Physical & Mechanical",
      zh: "物理与力学指标",
      es: "Físico y mecánico",
      de: "Physikalisch & mechanisch",
      fr: "Physique et mécanique",
      pt: "Físico e mecânico"
    },
    colElement: {
      en: "Element",
      zh: "元素",
      es: "Elemento",
      de: "Element",
      fr: "Élément",
      pt: "Elemento"
    },
    colStandard: {
      en: "Standard",
      zh: "标准要求",
      es: "Estándar",
      de: "Norm",
      fr: "Norme",
      pt: "Padrão"
    },
    colActual: {
      en: "Actual",
      zh: "实测值",
      es: "Real",
      de: "Ist-Wert",
      fr: "Mesuré",
      pt: "Real"
    },
    wireDiameterTolLabel: {
      en: "Wire Diameter Tolerance",
      zh: "金属丝径公差",
      es: "Tolerancia del diámetro de hilo",
      de: "Drahtdurchmessertoleranz",
      fr: "Tolérance du diamètre de fil",
      pt: "Tolerância do diâmetro do arame"
    },
    wireDiameterTolValue: {
      en: "± 0.002 mm (Pass)",
      zh: "± 0.002 mm (合格)",
      es: "± 0.002 mm (Conforme)",
      de: "± 0,002 mm (Bestanden)",
      fr: "± 0,002 mm (Conforme)",
      pt: "± 0,002 mm (Aprovado)"
    },
    apertureUniformityLabel: {
      en: "Aperture Uniformity",
      zh: "网孔孔径均匀度",
      es: "Uniformidad de apertura",
      de: "Gleichmäßigkeit der Maschenweite",
      fr: "Uniformité de l'ouverture",
      pt: "Uniformidade da abertura"
    },
    materialTraceabilityFoot: {
      en: "Material traceability",
      zh: "材料全流程可追溯",
      es: "Trazabilidad de materiales",
      de: "Materialrückverfolgbarkeit",
      fr: "Traçabilité des matériaux",
      pt: "Rastreabilidade de materiais"
    },
    sampleCertBtn: {
      en: "Sample Certificate",
      zh: "查看证书样本",
      es: "Certificado de muestra",
      de: "Musterzertifikat",
      fr: "Exemple de certificat",
      pt: "Certificado de amostra"
    },
    bottomCalloutHeading: {
      en: "Require Custom Material Verification or PMI Documentation?",
      zh: "需要特种材料检验报告或便携式 PMI 光谱分析文件？",
      es: "¿Requiere verificación de materiales a medida o documentación PMI?",
      de: "Benötigen Sie individuelle Materialprüfungen oder PMI-Dokumentationen?",
      fr: "Besoin d'une vérification matière sur mesure ou d'un rapport PMI ?",
      pt: "Necessita de verificação de material sob medida ou documentação PMI?"
    },
    bottomCalloutDesc: {
      en: "We supply full 3.1 chemical analysis, third-party lab inspection reports, and custom roll heat logs for critical pharmaceutical and aerospace applications.",
      zh: "我们支持为制药、航天及精细化工等高要求工况提供完整的 3.1 化学分析报告、第三方独立检测单据与出厂卷号追溯归档。",
      es: "Suministramos análisis químicos 3.1 completos, informes de laboratorio independientes y registros de colada por rollo para aplicaciones farmacéuticas y aeroespaciales críticas.",
      de: "Wir liefern vollständige 3.1-Analysen, unabhängige Laborprüfberichte und Schmelzenprotokolle je Rolle für kritische Anwendungen in Pharma und Luftfahrt.",
      fr: "Nous fournissons des analyses chimiques 3.1 complètes, des rapports d'inspection de laboratoires tiers et des registres de coulée par rouleau pour les applications critiques.",
      pt: "Fornecemos análises químicas 3.1 completas, relatórios de laboratórios terceirizados e registros de corrida por rolo para aplicações farmacêuticas e aeroespaciais críticas."
    },
    requestQuoteWithMtrBtn: {
      en: "Request Quote with MTR",
      zh: "索取含 MTR 报告报价",
      es: "Solicitar cotización con MTR",
      de: "Angebot mit MTR anfragen",
      fr: "Demander un devis avec MTR",
      pt: "Solicitar cotação com MTR"
    },
    coilTagsImageAlt: {
      en: "Stainless steel wire coils tagged with traceable melt heat numbers and barcode batch identification",
      zh: "附带炉号与条码标签的不锈钢母料盘圆批次追踪标签",
      es: "Bobinas de alambre de acero inoxidable etiquetadas con números de colada y código de barras",
      de: "Edelstahldrahtspulen mit rückverfolgbaren Schmelzen- und Barcode-Chargenanhängern",
      fr: "Bobines de fil inox étiquetées avec numéros de coulée et codes-barres de lot",
      pt: "Bobinas de arame de aço inoxidável com etiquetas de corrida e código de barras para rastreabilidade"
    }
  };
  return <section id="traceability" data-nav="light" className="relative mf-section bg-background text-foreground overflow-hidden">
      {/* Background Decorative Grid Lines (Kit Device) */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.03] dark:opacity-[0.07] bg-[radial-gradient(var(--border)_1px,transparent_1px)] [background-size:24px_24px]" />

      <div className="mf-container relative z-10">
        {/* Section Header */}
        <Reveal className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Badge variant="outline" className="px-3 py-1 mf-caption font-semibold tracking-wide uppercase border-primary/30 text-primary">
              <ShieldCheck className="size-3.5 mr-1" />
              {pick(lang, copy.badge)}
            </Badge>
          </div>
          <h2 className="mf-h2 text-foreground font-sans">
            {pick(lang, copy.h2)}
          </h2>
          <p className="mt-4 mf-lead text-muted-foreground leading-relaxed">
            {pick(lang, copy.lead)}
          </p>
        </Reveal>

        {/* Supporting Image: Coils with Heat/Lot Tags */}
        <Reveal delay={60} className="mt-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 bg-card border border-border rounded-lg p-5 items-center">
            <div className="mf-media-framed md:col-span-4 mf-ratio-card">
              <img src={siteImage("60e9ef-wire-coil-lot-heat-nu-b2d373.webp")} alt={pick(lang, copy.coilTagsImageAlt)} loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
            <div className="md:col-span-8 space-y-2">
              <div className="flex items-center gap-2">
                <Barcode className="size-4 text-primary" />
                <span className="font-mono text-sm font-semibold text-foreground uppercase tracking-wide">
                  {pick(lang, {
                  en: "Mill Heat Number & Traceability Barcoding",
                  zh: "特钢出厂炉号与批次条码追踪系统",
                  es: "Número de colada de acería y código de barras de trazabilidad",
                  de: "Werksschmelzennummer & Barcode-Rückverfolgbarkeit",
                  fr: "Numéro de coulée usine et codes-barres de traçabilité",
                  pt: "Número de corrida de usina e código de barras de rastreabilidade"
                })}
                </span>
              </div>
              <p className="mf-caption text-muted-foreground leading-relaxed">
                {pick(lang, {
                en: "Each incoming wire coil lot arrives with its certified Mill Test Report (MTR). Physical tags link each coil directly to its furnace heat number and chemical ladle analysis before wire weaving begins.",
                zh: "每批入库不锈钢丝卷均附带钢厂出厂质检证明 (MTR)。专用物理标牌在织造前直接关联熔炼炉号及熔融成分化验单，实现源头可信溯源。",
                es: "Cada lote de bobinas de alambre entrante llega con su informe MTR certificado, vinculando cada bobina directamente con su número de colada y análisis químico antes del tejido.",
                de: "Jedes eingehende Drahtlos wird mit zertifiziertem Werksprüfzeugnis geliefert. Anhänger verbinden jede Spule direkt mit ihrer Schmelzennummer und der Pfannenanalyse vor dem Weben.",
                fr: "Chaque lot de bobines de fil entrant est accompagné de son certificat MTR, reliant chaque bobine à son numéro de coulée et analyse de poche avant tissage.",
                pt: "Cada lote de bobinas de arame chega com seu relatório MTR certificado, associando cada bobina diretamente ao número de corrida e análise química antes da tecelagem."
              })}
              </p>
            </div>
          </div>
        </Reveal>

        {/* Grid Content Layout */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {/* Left Side: 4 Traceability Steps / Pillars */}
          <div className="lg:col-span-7 flex flex-col gap-5">
            {traceabilitySteps.map((step, i) => {
            const StepIcon = step.icon;
            return <Reveal key={step.id} delay={Math.min(i * 60, 300)}>
                  <Card className="border border-border/80 hover:border-primary/40 mf-hover-lift transition-all group">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4 sm:gap-5">
                        <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary shrink-0">
                          <StepIcon className="size-5" aria-hidden="true" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="mf-caption font-mono font-semibold text-muted-foreground uppercase tracking-wider">
                              {pick(lang, copy.phasePrefix)} {step.stepNumber}
                            </span>
                          </div>
                          <h3 className="mf-h3 text-foreground group-hover:text-primary transition-colors">
                            {pick(lang, step.title)}
                          </h3>
                          <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                            {pick(lang, step.description)}
                          </p>
                          
                          <div className="mt-4 flex flex-wrap gap-2">
                            {step.highlights.map((h, hIdx) => <span key={hIdx} className="inline-flex items-center gap-1.5 mf-caption font-medium bg-muted text-muted-foreground px-2.5 py-1 rounded-md">
                                <CircleCheck className="size-3.5 text-primary" aria-hidden="true" />
                                <span>{pick(lang, h)}</span>
                              </span>)}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Reveal>;
          })}
          </div>

          {/* Right Side: Material Test Report Mockup */}
          <Reveal delay={180} className="lg:col-span-5">
            <Card className="border border-border bg-card rounded-lg overflow-hidden sticky top-24">
              {/* Inspection Card Header */}
              <div className="bg-primary/5 p-5 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="size-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
                    <ClipboardList className="size-5" />
                  </div>
                  <div>
                    <div className="mf-caption font-mono font-medium text-muted-foreground uppercase">
                      {pick(lang, copy.liveSpecVerification)}
                    </div>
                    <CardTitle className="text-base font-bold text-foreground">
                      {pick(lang, copy.certTitle)}
                    </CardTitle>
                  </div>
                </div>
                <Badge variant="default" className="bg-emerald-600 text-white font-mono mf-caption">
                  {pick(lang, copy.verifiedPass)}
                </Badge>
              </div>

              <CardContent className="p-5 space-y-5">
                {/* Heat & Batch Meta */}
                <div className="grid grid-cols-2 gap-3 p-3 bg-muted/50 rounded-lg mf-caption font-mono">
                  <div>
                    <span className="text-muted-foreground block mf-caption uppercase">
                      {pick(lang, copy.heatNumberLabel)}
                    </span>
                    <span className="font-semibold text-foreground font-mono">H-88204-316L</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block mf-caption uppercase">
                      {pick(lang, copy.alloyGradeLabel)}
                    </span>
                    <span className="font-semibold text-foreground font-mono">AISI 316L / 1.4404</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block mf-caption uppercase">
                      {pick(lang, copy.meshCountLabel)}
                    </span>
                    <span className="font-semibold text-foreground font-mono">200 Mesh x 0.05mm</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block mf-caption uppercase">
                      {pick(lang, copy.lotRollIdLabel)}
                    </span>
                    <span className="font-semibold text-foreground font-mono">MF-2025-0941</span>
                  </div>
                </div>

                {/* Tab Switcher */}
                <div className="flex border-b border-border">
                  <Button type="button" variant="ghost" onClick={() => setActiveTab("chem")} className={`h-auto pb-2 px-3 mf-caption font-medium transition-colors border-b-2 cursor-pointer rounded-none hover:bg-transparent ${activeTab === "chem" ? "border-primary text-primary font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                    {pick(lang, copy.tabChem)}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setActiveTab("mech")} className={`h-auto pb-2 px-3 mf-caption font-medium transition-colors border-b-2 cursor-pointer rounded-none hover:bg-transparent ${activeTab === "mech" ? "border-primary text-primary font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                    {pick(lang, copy.tabMech)}
                  </Button>
                </div>

                {/* Tab Contents */}
                {activeTab === "chem" ? <div className="space-y-2.5">
                    <div className="grid grid-cols-12 mf-caption font-semibold text-muted-foreground pb-1 border-b border-border/60">
                      <span className="col-span-5">{pick(lang, copy.colElement)}</span>
                      <span className="col-span-4">{pick(lang, copy.colStandard)}</span>
                      <span className="col-span-3 text-right">{pick(lang, copy.colActual)}</span>
                    </div>
                    {chemicalComposition.map((item, idx) => <div key={idx} className="grid grid-cols-12 mf-caption items-center py-1">
                        <span className="col-span-5 font-medium text-foreground flex items-center gap-1.5">
                          <Check className="size-3.5 text-emerald-600 shrink-0" aria-hidden="true" />
                          <span>{item.element}</span>
                        </span>
                        <span className="col-span-4 text-muted-foreground font-mono">{item.standard}</span>
                        <span className="col-span-3 text-right font-mono font-semibold text-foreground">{item.measured}</span>
                      </div>)}
                  </div> : <div className="space-y-3 mf-caption">
                    <div className="p-3 bg-background rounded border border-border/80 flex items-center justify-between">
                      <span className="text-muted-foreground">{pick(lang, copy.wireDiameterTolLabel)}</span>
                      <span className="font-mono font-semibold text-foreground">{pick(lang, copy.wireDiameterTolValue)}</span>
                    </div>
                    <div className="p-3 bg-background rounded border border-border/80 flex items-center justify-between">
                      <span className="text-muted-foreground">{pick(lang, copy.apertureUniformityLabel)}</span>
                      <span className="font-mono font-semibold text-foreground">ISO 9044 Class 1</span>
                    </div>
                  </div>}

                <Separator />

                {/* Bottom CTA & Verification Link */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-1">
                  <div className="flex items-center gap-2 mf-caption text-muted-foreground">
                    <Cpu className="size-4 text-primary" />
                    <span>{pick(lang, copy.materialTraceabilityFoot)}</span>
                  </div>
                  
                  <Button asChild variant="outline" size="sm" className="w-full sm:w-auto mf-caption gap-1.5">
                    <Link to="/DatasheetsPage">
                      <FileText className="size-3.5" aria-hidden="true" />
                      <span>{pick(lang, copy.sampleCertBtn)}</span>
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </Reveal>

        </div>

        {/* Bottom Callout Bar: Clean surface-dark band instead of bg-secondary */}
        <Reveal delay={240} className="mt-16 p-6 sm:p-8 rounded-lg bg-surface-dark text-surface-dark-foreground border border-surface-dark-border flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="inline-flex size-10 items-center justify-center rounded-md border border-surface-dark-border bg-surface-dark-elevated text-surface-dark-foreground shrink-0">
              <Search className="size-5" aria-hidden="true" />
            </div>
            <div>
              <h3 className="mf-h3 text-surface-dark-foreground">
                {pick(lang, copy.bottomCalloutHeading)}
              </h3>
              <p className="mf-caption text-surface-dark-muted mt-1">
                {pick(lang, copy.bottomCalloutDesc)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0 w-full md:w-auto">
            <Link to="/RequestAQuotePage" className="inline-flex items-center justify-center text-accent hover:underline font-semibold mf-caption gap-1.5 whitespace-nowrap">
              <span>{pick(lang, copy.requestQuoteWithMtrBtn)}</span>
              <ArrowRight className="size-3.5" aria-hidden="true" />
            </Link>
          </div>
        </Reveal>

      </div>
    </section>;
}